import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format, isValid, parseISO } from "date-fns";
import {
  Copy,
  ExternalLink,
  Info,
  Plus,
  Save,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { APT } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { canAccessMainTab, canWriteMainTab } from "@/lib/maintabAccess";
import {
  APPOINTMENT_STATUSES,
  CONFIRMATION_STATUSES,
  DURATION_OPTIONS,
  PLANNED_CLASSES,
  REMINDER_METHODS,
  REMINDER_OFFSETS,
  REPEAT_OPTIONS,
  VISIT_TYPE_OPTIONS,
  addMinutesToTime,
  appointmentsCalendarPath,
  createAppointment,
  formFromAppointment,
  getAppointment,
  listSchedulingProviders,
  resolveAppointmentLocationId,
  safeAppointmentReturnPath,
  safeChartReturnPath,
  searchPatients,
  toAppointmentPayload,
  updateAppointment,
  visitTypeLabel,
  confirmationStatusLabel,
} from "@/lib/appointmentApi";
import { fetchLocations, fetchPatients, patientNameOf } from "@/lib/encounterApi";
import { ageFromBirthDate, formatDate, formatGender, patientInitials, patientPhone } from "@/lib/patientChart";
import { mrnOf } from "@/lib/patientApi";
import { listReferringProviders } from "@/lib/referringProviderApi";
import { cn } from "@/lib/utils";
import "@/styles/emr-appointment-form.css";

function todayIso() {
  return format(new Date(), "yyyy-MM-dd");
}

function emptyForm(defaults = {}) {
  return {
    patientId: "",
    locationId: defaults.locationId || "",
    providerUserId: defaults.providerUserId || "",
    referringPractitionerId: "",
    visitType: "390906007",
    plannedClass: "AMB",
    reason: "",
    comment: "",
    patientInstruction: "",
    date: todayIso(),
    startTime: "10:00",
    duration: "15",
    status: "booked",
    confirmationStatus: "confirmed",
    reminderMethod: "sms",
    reminderOffset: "24h",
    recurrence: "does-not-repeat",
    resourceName: "",
  };
}

function buildTimeOptions(current) {
  const options = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 15, 30, 45]) {
      const code = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      options.push({ code, display: formatTimeLabel(code) });
    }
  }
  if (current && !options.some((o) => o.code === current)) {
    options.push({ code: current, display: formatTimeLabel(current) });
    options.sort((a, b) => a.code.localeCompare(b.code));
  }
  return options;
}

function RowField({ label, required, children, className, align = "center" }) {
  return (
    <div
      className={cn(
        "emr-aptform__row",
        align === "start" && "emr-aptform__row--start",
        className,
      )}
    >
      <Label className="emr-aptform__label">
        {label}
        {required ? <span className="emr-aptform__req"> *</span> : null}
      </Label>
      <div className="emr-aptform__control">{children}</div>
    </div>
  );
}

function CompactSection({ title, children, className }) {
  return (
    <section className={cn("card emr-aptform__section", className)}>
      <h2 className="emr-aptform__section-title">{title}</h2>
      {children}
    </section>
  );
}

function SelectField({ value, onChange, options, testId, placeholder, className }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={cn("emr-aptform__select", className)}>
        <SelectValue placeholder={placeholder || "Select"} />
      </SelectTrigger>
      <SelectContent position="popper" className="emr-aptform-select z-[200] max-h-96">
        {options.map((o) => (
          <SelectItem key={o.code} value={o.code}>
            {o.display}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function RadioField({ value, onChange, options, testId }) {
  return (
    <RadioGroup
      data-testid={testId}
      value={value}
      onValueChange={onChange}
      className="emr-aptform__radios"
    >
      {options.map((o) => (
        <div key={o.code} className="emr-aptform__radio">
          <RadioGroupItem
            value={o.code}
            id={`${testId}-${o.code}`}
            className="emr-aptform__radio-input"
          />
          <Label htmlFor={`${testId}-${o.code}`} className="emr-aptform__radio-label">
            {o.display}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );
}

function formatTimeLabel(value) {
  if (!value) return "—";
  const [h, m] = value.split(":");
  const date = new Date();
  date.setHours(Number(h), Number(m), 0, 0);
  return format(date, "h:mm a");
}

function formatPreviewDate(value) {
  if (!value) return "—";
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, "EEE MMM d").toUpperCase() : value;
}

const REMINDER_RADIO_OPTIONS = REMINDER_METHODS.map((o) => ({
  code: o.code,
  display:
    o.code === "sms" ? "Text" : o.code === "none" ? "No reminder" : o.display.replace(" Call", "").replace(" Message", ""),
}));

const TIMING_RADIO_OPTIONS = REMINDER_OFFSETS.map((o) => ({
  code: o.code,
  display: o.display.replace(" before", ""),
}));

export default function NewAppointmentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { appointmentId } = useParams();
  const isEdit = Boolean(appointmentId);
  const { user, context } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "appointments");
  const canReadPatients = canAccessMainTab(user?.maintabAccess, "patients");
  const canReadReferring = canAccessMainTab(user?.maintabAccess, "referring-providers");
  const queryLocation = searchParams.get("location") || "";
  const queryPatient = searchParams.get("patient") || "";
  const returnPath = safeAppointmentReturnPath(searchParams.get("return"));
  const [form, setForm] = useState(() => emptyForm({ locationId: queryLocation, patientId: queryPatient }));
  const [patients, setPatients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [providers, setProviders] = useState([]);
  const [referringProviders, setReferringProviders] = useState([]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      canReadPatients ? fetchPatients() : Promise.resolve([]),
      fetchLocations(),
      listSchedulingProviders(),
      canReadReferring ? listReferringProviders({ active: true }) : Promise.resolve({ items: [] }),
      isEdit ? getAppointment(appointmentId) : Promise.resolve(null),
    ])
      .then(([plist, llist, provs, refs, existing]) => {
        if (cancelled) return;
        setPatients(plist);
        setLocations(llist);
        setProviders(provs);
        setReferringProviders(refs.items || []);
        const contextLoc = context?.location?.id || (context?.location?.reference || "").split("/")[1];
        const defaultProvider =
          (user?.isClinician && user?.id) ||
          provs.find((p) => (p.roles || []).includes("md_do"))?.id ||
          provs[0]?.id ||
          "";
        if (existing) {
          setForm(formFromAppointment(existing));
          return;
        }
        setForm((prev) => ({
          ...prev,
          locationId:
            prev.locationId && llist.some((l) => l.id === prev.locationId)
              ? prev.locationId
              : resolveAppointmentLocationId({
                  queryLocation,
                  contextLocation: contextLoc,
                  locations: llist,
                }),
          patientId:
            prev.patientId && plist.some((p) => p.id === prev.patientId)
              ? prev.patientId
              : queryPatient && plist.some((p) => p.id === queryPatient)
                ? queryPatient
                : prev.patientId,
          providerUserId: prev.providerUserId || defaultProvider,
        }));
      })
      .catch(() => {
        if (cancelled) return;
        if (isEdit) {
          toast.error("Could not load appointment.");
          navigate(returnPath || "/appointments");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingEdit(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId, canReadPatients, canReadReferring, context, isEdit, navigate, queryLocation, queryPatient, returnPath, user]);

  const selectedPatient = patients.find((p) => p.id === form.patientId);
  const selectedLocation = locations.find((l) => l.id === form.locationId);
  const selectedProvider = providers.find((p) => p.id === form.providerUserId);
  const selectedReferring = referringProviders.find((p) => p.providerId === form.referringPractitionerId);
  const matches = searchPatients(patients, query);
  const endTime = addMinutesToTime(form.startTime, form.duration);

  const preview = useMemo(
    () => ({
      date: formatPreviewDate(form.date),
      time: `${formatTimeLabel(form.startTime)} – ${formatTimeLabel(endTime)} (${form.duration} min)`,
      patient: selectedPatient ? patientNameOf(selectedPatient) : "No patient selected",
      mrn: selectedPatient ? mrnOf(selectedPatient) : "",
      visit: visitTypeLabel(form.visitType) || "—",
      reason: form.reason || "—",
      location: [selectedLocation?.name, form.resourceName].filter(Boolean).join(" · ") || "—",
      provider: selectedProvider?.displayName || "—",
      referring: selectedReferring
        ? `${selectedReferring.firstName} ${selectedReferring.lastName}, ${selectedReferring.credentials}`
        : "—",
    }),
    [form, selectedPatient, selectedLocation, selectedProvider, selectedReferring, endTime],
  );

  const validate = () => {
    if (!form.patientId) return "Patient is required.";
    if (!form.locationId) return "Office Location is required.";
    if (!form.date || !form.startTime) return "Date and start time are required.";
    return null;
  };

  const durationOptions = DURATION_OPTIONS.some((option) => option.code === form.duration)
    ? DURATION_OPTIONS
    : [{ code: form.duration, display: `${form.duration} min` }, ...DURATION_OPTIONS];
  const timeOptions = useMemo(() => buildTimeOptions(form.startTime), [form.startTime]);
  const calendarReturn = returnPath || appointmentsCalendarPath({ locationId: form.locationId, date: form.date });

  const persist = async ({ reset, keepCopy }) => {
    if (!canWrite) return;
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    if (isEdit && (reset || keepCopy)) {
      return;
    }
    setSaving(true);
    try {
      const payload = toAppointmentPayload(form);
      if (isEdit) {
        await updateAppointment(appointmentId, payload);
        toast.success("Appointment updated.");
        navigate(
          safeChartReturnPath(searchParams.get("return")) ||
            appointmentsCalendarPath({ locationId: form.locationId, date: form.date }),
        );
        return;
      }
      await createAppointment(payload);
      toast.success("Appointment scheduled.");
      if (reset) {
        setForm(emptyForm({
          locationId: form.locationId,
          providerUserId: form.providerUserId,
          patientId: form.patientId,
        }));
        setQuery("");
        return;
      }
      if (keepCopy) return;
      navigate(calendarReturn);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : detail;
      toast.error(msg || "Failed to save appointment.");
    } finally {
      setSaving(false);
    }
  };

  const moduleClass = (...mods) =>
    cn("emr-aptform__module", ...mods.map((mod) => `emr-aptform__module--${mod}`));

  if (loadingEdit) {
    return (
      <div data-testid={APT.formPage} className="emr-aptform">
        <h1 data-testid={APT.formTitle} className="emr-aptform__title">
          Edit Appointment
        </h1>
        <p className="emr-aptform__status-msg">Loading appointment…</p>
      </div>
    );
  }

  return (
    <div data-testid={APT.formPage} className={cn("emr-aptform", isEdit ? "emr-aptform--edit" : "emr-aptform--new")}>
      <div className="emr-aptform__pagehead">
        <div className="min-w-0">
          <h1 data-testid={APT.formTitle} className="emr-aptform__title">
            {isEdit ? "Edit Appointment" : "New Appointment"}
          </h1>
          <p className="emr-aptform__subtitle">
            {isEdit
              ? "Update the scheduled appointment. Saving does not create an Encounter."
              : "Schedule and manage a patient appointment."}
          </p>
        </div>
        <div className="emr-aptform__head-actions">
          <button
            type="button"
            className="btn emr-aptform__cancel"
            data-testid={APT.cancelBtn}
            onClick={() => navigate(calendarReturn)}
          >
            Cancel
          </button>
          {canWrite ? (
            <button
              type="button"
              className="btn emr-aptform__save"
              disabled={saving || loadingEdit}
              onClick={() => persist({})}
            >
              {isEdit ? "Save Changes" : "Save"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="emr-aptform__modules">
      <CompactSection title="Patient" className={moduleClass("patient")}>
        <RowField label="Patient" required>
          <div className="emr-aptform__search">
            <Search className="emr-aptform__search-icon" aria-hidden="true" />
            <Input
              data-testid={APT.patientSearch}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, DOB, MRN or phone number..."
              className="emr-aptform__input emr-aptform__search-input"
            />
            {query && !selectedPatient && (
              <div className="emr-aptform__results">
                {matches.length ? (
                  matches.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`${APT.patientResult}-${p.id}`}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, patientId: p.id }));
                        setQuery("");
                      }}
                      className="emr-aptform__result"
                    >
                      <span className="emr-aptform__result-name">{patientNameOf(p)}</span>
                      <span className="emr-aptform__result-meta">
                        {mrnOf(p)} · {formatDate(p.birthDate)} · {patientPhone(p) || "No phone"}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="emr-aptform__results-empty">No matching patients.</div>
                )}
              </div>
            )}
          </div>
        </RowField>

        {selectedPatient ? (
          <div data-testid={APT.selectedPatient} className="emr-aptform__indent emr-aptform__patient">
            <div className="emr-aptform__avatar">{patientInitials(selectedPatient)}</div>
            <span className="emr-aptform__patient-name">{patientNameOf(selectedPatient)}</span>
            <span className="emr-aptform__mrn">{mrnOf(selectedPatient)}</span>
            <span className="emr-aptform__patient-meta">
              DOB: {formatDate(selectedPatient.birthDate)}
              {ageFromBirthDate(selectedPatient.birthDate) != null
                ? ` (${ageFromBirthDate(selectedPatient.birthDate)})`
                : ""}{" "}
              · {formatGender(selectedPatient.gender)}
            </span>
            <span className="emr-aptform__patient-meta">Phone: {patientPhone(selectedPatient) || "—"}</span>
            {canReadPatients ? (
              <Link
                to={`/patients/${selectedPatient.id}`}
                data-testid={APT.viewChart}
                className="emr-aptform__chart-link"
              >
                View chart <ExternalLink className="emr-aptform__icon" />
              </Link>
            ) : null}
            <span data-testid={APT.noShowRate} className="emr-aptform__noshow">
              No-show: Not available
            </span>
          </div>
        ) : (
          <p className="emr-aptform__indent emr-aptform__hint">
            Search and select a patient to schedule.
          </p>
        )}
      </CompactSection>

      <CompactSection title="When" className={moduleClass("when")}>
        <div className="emr-aptform__cluster emr-aptform__cluster--when">
          <RowField label="Date" required>
            <Input
              data-testid={APT.date}
              type="date"
              value={form.date}
              onChange={(e) => set("date")(e.target.value)}
              className="emr-aptform__input emr-aptform__date"
            />
          </RowField>
          <RowField label="Start Time" required>
            <SelectField
              value={form.startTime}
              onChange={set("startTime")}
              options={timeOptions}
              testId={APT.startTime}
              className="emr-aptform__time"
            />
          </RowField>
          <RowField label="End Time">
            <span data-testid={APT.endTime} className="emr-aptform__end">
              {formatTimeLabel(endTime)}
            </span>
          </RowField>
          <RowField label="Repeat">
            <SelectField
              value={form.recurrence}
              onChange={set("recurrence")}
              options={REPEAT_OPTIONS}
              testId={APT.repeat}
              className="emr-aptform__select--sm"
            />
          </RowField>
        </div>
        <RowField label="Duration">
          <RadioField
            value={form.duration}
            onChange={set("duration")}
            options={durationOptions.map((o) => ({
              code: o.code,
              display: o.display.replace(" minutes", " min").replace(" minute", " min"),
            }))}
            testId={APT.duration}
          />
        </RowField>
        <RowField label="Confirmation">
          <RadioField
            value={form.confirmationStatus}
            onChange={set("confirmationStatus")}
            options={CONFIRMATION_STATUSES}
            testId={APT.confirmation}
          />
        </RowField>
      </CompactSection>

      <CompactSection title="Where & Who" className={moduleClass("where")}>
        <div className="emr-aptform__cluster emr-aptform__cluster--split">
          <RowField label="Office Location" required>
            <SelectField
              value={form.locationId}
              onChange={set("locationId")}
              options={locations.map((l) => ({ code: l.id, display: l.name }))}
              testId={APT.officeLocation}
              placeholder="Select office location"
            />
          </RowField>
          <RowField label="Provider">
            <SelectField
              value={form.providerUserId}
              onChange={set("providerUserId")}
              options={providers.map((p) => ({ code: p.id, display: p.displayName }))}
              testId={APT.provider}
            />
          </RowField>
          <RowField label="Resource / Room">
            <Input
              data-testid={APT.resource}
              value={form.resourceName}
              onChange={(e) => set("resourceName")(e.target.value)}
              placeholder="Exam room if used"
              className="emr-aptform__input"
            />
          </RowField>
          <RowField label="Referring Provider">
            {selectedReferring ? (
              <div data-testid={APT.referringProvider} className="emr-aptform__referring">
                <div className="min-w-0">
                  <p className="emr-aptform__referring-name">
                    {selectedReferring.firstName} {selectedReferring.lastName}, {selectedReferring.credentials}
                  </p>
                  <p className="emr-aptform__referring-org">
                    {selectedReferring.organization}
                    {selectedReferring.location ? ` • ${selectedReferring.location}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => set("referringPractitionerId")("")}
                  aria-label="Clear referring provider"
                  className="emr-aptform__clear"
                >
                  <X className="emr-aptform__icon" />
                </button>
              </div>
            ) : (
              <SelectField
                value={form.referringPractitionerId}
                onChange={set("referringPractitionerId")}
                options={referringProviders.map((p) => ({
                  code: p.providerId,
                  display: `${p.firstName} ${p.lastName}, ${p.credentials}`,
                }))}
                testId={APT.referringProvider}
                placeholder="Select referring provider"
              />
            )}
          </RowField>
        </div>
      </CompactSection>

      <CompactSection title="Visit" className={moduleClass("visit")}>
        <RowField label="Visit Type">
          <RadioField
            value={form.visitType}
            onChange={set("visitType")}
            options={VISIT_TYPE_OPTIONS}
            testId={APT.visitType}
          />
        </RowField>
        <RowField label="Encounter Class">
          <SelectField
            value={form.plannedClass}
            onChange={set("plannedClass")}
            options={PLANNED_CLASSES}
            testId={APT.encounterClass}
            className="emr-aptform__select--sm"
          />
        </RowField>
        <RowField label="Reason">
          <Input
            data-testid={APT.reason}
            value={form.reason}
            onChange={(e) => set("reason")(e.target.value)}
            placeholder="Chief complaint"
            className="emr-aptform__input"
          />
        </RowField>
        <RowField label="Notes" align="start">
          <div className="min-w-0">
            <Textarea
              data-testid={APT.comment}
              value={form.comment}
              maxLength={500}
              onChange={(e) => set("comment")(e.target.value)}
              className="emr-aptform__input"
            />
            <div className="emr-aptform__count">{form.comment.length} / 500</div>
          </div>
        </RowField>
      </CompactSection>

      <CompactSection title="Reminders" className={moduleClass("reminders")}>
        <div className="emr-aptform__cluster emr-aptform__cluster--when">
          <RowField label="Reminder" className="emr-aptform__row--grow">
            <RadioField
              value={form.reminderMethod}
              onChange={set("reminderMethod")}
              options={REMINDER_RADIO_OPTIONS}
              testId={APT.reminderMethod}
            />
          </RowField>
          <RowField label="Timing" className="emr-aptform__row--grow">
            <RadioField
              value={form.reminderOffset}
              onChange={set("reminderOffset")}
              options={TIMING_RADIO_OPTIONS}
              testId={APT.reminderOffset}
            />
          </RowField>
        </div>
        <div className="emr-aptform__indent emr-aptform__note">
          <Info className="emr-aptform__note-icon" aria-hidden="true" />
          Appointment reminders are stored with the appointment. Automatic sending
          is not enabled. Use Send SMS on the appointment details dialog for a
          SANDBOX test.
        </div>
        <RowField label="Patient Instructions" align="start">
          <Textarea
            data-testid={APT.patientInstruction}
            value={form.patientInstruction}
            onChange={(e) => set("patientInstruction")(e.target.value)}
            className="emr-aptform__input"
            placeholder="Instructions for reminder"
          />
        </RowField>
      </CompactSection>

      <CompactSection title="Status" className={moduleClass("status")}>
        <RowField label="Status">
          <RadioField
            value={form.status}
            onChange={set("status")}
            options={APPOINTMENT_STATUSES}
            testId={APT.status}
          />
        </RowField>
        <div className="emr-aptform__indent emr-aptform__note">
          <Info className="emr-aptform__note-icon" aria-hidden="true" />
          {isEdit
            ? "Saving updates this appointment only. It does not create or change a clinical Encounter."
            : "Saving this appointment does not start clinical care or create an Encounter. Use Start Encounter from the appointment after the patient arrives."}
        </div>
      </CompactSection>

      <section data-testid={APT.preview} className={cn("card emr-aptform__section", moduleClass("wide"))}>
        <h2 className="emr-aptform__section-title">Appointment preview</h2>
        <div className="emr-aptform__preview-body">
          <div className="emr-aptform__preview-meta">
            <span className="emr-aptform__preview-date">{preview.date}</span>
            <span className="emr-aptform__preview-strong">{preview.time}</span>
            <span className="emr-aptform__preview-strong">{preview.patient}</span>
            {preview.mrn ? <span className="emr-aptform__mrn">{preview.mrn}</span> : null}
            <span className="emr-aptform__chip">{confirmationStatusLabel(form.confirmationStatus)}</span>
            <span className="emr-aptform__preview-muted">{preview.visit}</span>
            <span className="emr-aptform__preview-muted">{preview.reason}</span>
            <span className="emr-aptform__preview-muted">{preview.location}</span>
            <span className="emr-aptform__preview-muted">{preview.provider}</span>
            <span className="emr-aptform__preview-muted">{preview.referring}</span>
          </div>
          <div className="emr-aptform__preview-actions">
            {canWrite ? (
              <button
                type="button"
                className="btn emr-aptform__save"
                data-testid={APT.saveBtn}
                disabled={saving || loadingEdit}
                onClick={() => persist({})}
                title={isEdit ? "Save changes" : "Save"}
              >
                <Save className="emr-aptform__icon" aria-hidden="true" />
                {isEdit ? "Save Changes" : "Save"}
              </button>
            ) : null}
            {canWrite && !isEdit ? (
              <>
                <button
                  type="button"
                  className="btn emr-aptform__secondary"
                  data-testid={APT.saveNewBtn}
                  disabled={saving}
                  onClick={() => persist({ reset: true })}
                >
                  <Plus className="emr-aptform__icon" aria-hidden="true" />
                  Save & New
                </button>
                <button
                  type="button"
                  className="btn emr-aptform__secondary"
                  data-testid={APT.saveCopyBtn}
                  disabled={saving}
                  onClick={() => persist({ keepCopy: true })}
                >
                  <Copy className="emr-aptform__icon" aria-hidden="true" />
                  Save & Copy
                </button>
              </>
            ) : null}
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}
